import React, { useState, useEffect } from 'react';
import { convertToINRabbreviations, formatCalculatorValues, isAgentEuin, isBtoCFlow, isMintproApp } from '__utils/common-utils';
import styled from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';
import { get, getDefaultHeaders, post } from '__utils/ajax-wrapper';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { CommonStyledComponentProps } from '__utils/type';
import "react-datepicker/dist/react-datepicker.css";
import { Row, Col } from 'antd';
import BarChart from 'components/charts/bar-chart';
import type { EChartsOption } from "echarts";
import html2canvas from 'html2canvas';
import { ENDPOINTS } from 'constants/endpoints';
import ShareCalculatorPopup from '../share-calculator-popup';
import investmentHandImg from 'images/investment_hand.svg';
import investmentMoneyImg from 'images/investment-money.svg';
import './index.css';
import { UpAGreenArrowIcon } from 'assets/icons/up-green-arrow';
import InvestmentCalculatorSelector from '../investment-calculator.selector';
import { propsToJS } from '__utils/immutable-to-js';
import { setShareCalculatorCustomerDetails, shareCalculatorViaWhatsapp } from '../investment-calculator.actions';
import UserSelector from 'views/user/user.selector';
import InterestedUserPopup from '../interested-user-popup';
import whatsapp from "images/whatsapp.svg";
import { BlackBgRoundButton } from 'components/button';
import { fetchAgentCertificates } from 'views/user/user.actions';
import { getAgentCode } from '__utils/storage';
import useFlashToggler from 'hooks/use-flash-toggler';
import { isProUser } from '__utils/common-utils';
interface CalculationResultProps {
    graphData: any,
    totalInvestment: string,
    gains: string,
    resultTitle: string | React.ReactElement,
    resultAmount: any,
    resultBanner?: React.ReactElement
}

const CalculationCard = styled.div<any>`
    box-shadow: 4px 4px 20px rgba(106, 106, 106, 0.1);
    border-radius: 20px;
   .div-flex{
        display: flex;
        flex-direction: ${(props: any) => props.isMobile ? 'row' : 'column'};
        gap: ${(props: any) => props.isMobile ? '' : '20px'};
        justify-content: space-between;
        margin-bottom: 24px;
   }
   .result-title{
        max-width: ${(props: any) => props.isMobile ? '120px' : ''};
        font-size: ${(props: any) => props.isMobile ? '14px' : '16px'};
        font-family: Montserrat;
   }
   .amountFont{
        font-size: ${(props: any) => props.isMobile ? '30px' : '34px'};
        font-family: Montserrat;

   }
`
const InvestmentFloatBox = styled.div<any>`
    display: flex;
    gap: 6px;
    align-items: center;
    justify-content: left;
    .title-font{
        font-size: ${(props: any) => props.isMobile ? '14px' : '16px'};
    }
    .text-ellipsis {
        font-size: ${(props: any) => props.isMobile ? '16px' : '18px'};
        font-weight: bold;
        max-width: 200px;
    }
    div{
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100px;
    }
    
`
const ShareBtnContainer = styled.div<CommonStyledComponentProps>`
    margin-top: -25px;
    text-align: center;
`
const ShareCalcButton = styled(BlackBgRoundButton) <CommonStyledComponentProps>`
    width: ${(props: any) => props.isMobile ? '170px' : '200px'};
    font-size: 16px;
    .whatsappIcon{
        margin: 3px 4px 0 0;
    }
    height: 50px;
`
const CalculationResult: React.FC<CalculationResultProps> = ({ graphData, totalInvestment, gains, resultTitle, resultAmount, resultBanner }) => {

    const dispatch = useDispatch();
    const flash = useFlashToggler();
    const isMobile = useMediaQuery({ maxWidth: 767 });
    const { shareCalculatorConfig, shareCalculatorImage, shareWithCustomerDetails, customerFromSharedLinkDetails } = propsToJS(useSelector(InvestmentCalculatorSelector));
    const [showSharePopup, setShowSharePopup] = useState<boolean>(false);
    const printRef = React.useRef<HTMLDivElement>(null);
    const { agentCertificates, isAgentNotAllowed } = propsToJS(useSelector(UserSelector));

    useEffect(() => {
        if (!agentCertificates) {
            dispatch(fetchAgentCertificates(getAgentCode() || ''))
        }
    }, [])

    const echartOptions: EChartsOption = {
        grid: { bottom: 20, containLabel: true, left: 0, right: 0, top: 20, height: 160 },
        xAxis: {
            type: 'category',
            data: graphData.xAxisData,
            axisLine: {
                show: false
            },
            axisTick: {
                show: false
            },
        },
        yAxis: {
            type: 'value',
            axisLine: {
                show: false
            },
            axisTick: {
                show: false
            },
            splitLine: {
                show: false
            },
            axisLabel: {
                formatter: (value: any) => formatCalculatorValues(value)
            }
        },
        series:
            [
                {
                    name: "invested amount",
                    data: graphData.invested,
                    type: "bar",
                    stack: "total",
                    itemStyle: {
                        borderRadius: 2,
                        color: '#949497'

                    },
                    emphasis: {
                        itemStyle: {
                            color: '#3A3636'
                        }
                    },

                },
                {
                    name: "gains",
                    data: graphData.gains,
                    type: "bar",
                    stack: "total",
                    itemStyle: {
                        borderRadius: [2, 2, 0, 0],
                        color: '#80E4A8'
                    },
                    emphasis: {
                        itemStyle: {
                            color: '#01B97A'
                        }
                    },
                }
            ],
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow'
            },
            extraCssText: 'z-index: 999',
            valueFormatter: (value: any) => formatCalculatorValues(value)
        },
        animationEasing: "linear",
        animationDuration: 0

    }

    const onShareApiCall = () => {
        dispatch(shareCalculatorViaWhatsapp(false));
        dispatch(setShareCalculatorCustomerDetails(shareWithCustomerDetails, false));
        setShowSharePopup(false);
    }

    //@purpose: take ss of result & share 
    const shareScreenshot = async () => {
        const { clientMobile } = shareWithCustomerDetails;
        const detaislWithAgentNumber = { ...shareCalculatorConfig.shareData, agentNumber: agentCertificates?.advisorContact };

        const element = printRef.current!;
        const canvas = await html2canvas(element, { scale: 1 });
        const data = canvas.toDataURL('image/png');

        onShareApiCall();
        post(ENDPOINTS.SHARE_CALCULATOR_VIA_WHATSAPP,
            {
                calculatorImage: data,
                ...detaislWithAgentNumber,
                originalUrl: shareCalculatorConfig?.originalUrl
            },
            getDefaultHeaders(), true)
            .subscribe((response: AjaxResponse | AjaxError) => {
                if (response.response?.data && response.response?.data?.shortenUrl) {
                    const result = response.response?.data;
                    const returnShareMsg = () => {
                        return `${shareCalculatorConfig?.shareMessage}\n${result?.shortenUrl}`
                    }

                    if (isMintproApp()) {
                        const featurePayload = {
                            type: 'shareFileToWhatsapp',
                            payload: {
                                fileType: "image",
                                fileMeta: {
                                    fileId: result.fileId,
                                    fileName: result.fileName
                                },
                                intent: "WHATSAPP",
                                message: returnShareMsg(),
                            },
                        };
                        window.postMessage(JSON.stringify(featurePayload), '*');
                    }

                    else {
                        window.open(`https://api.whatsapp.com/send?phone=${clientMobile || ''}&text=` + window.encodeURIComponent(returnShareMsg()), '_blank');
                    }

                }
                else { flash.error('Failed to share') }


            }, (error: AjaxError) => {
                flash.error('Failed to share');
            });
    }

    useEffect(() => {
        shareCalculatorImage && shareScreenshot();
    }, [shareCalculatorImage])

 

    return (
        <>
            <CalculationCard isMobile={isMobile} ref={printRef}>
                <div className='yellow-calculator-grid'>
                    <div className='result-title'>{resultTitle}</div>
                    <p className='amountFont text-ellipsis'><strong>₹ {convertToINRabbreviations(resultAmount)}</strong></p>
                </div>
                <div>{resultBanner}</div>
                <div className='p16 result-box'>
                    <Row justify='space-between' align='middle' style={{ flexDirection: isMobile ? 'row' : 'row-reverse' }}>
                        <Col xs={24} md={7}>
                            <div className='div-flex'>
                                <div style={{ marginLeft: '-4px' }}>
                                    <InvestmentFloatBox>
                                        <img src={investmentHandImg} alt="investment" />
                                        <div>
                                            <p className='title-font'>You Invest</p>
                                            <p className='text-ellipsis'>₹ {formatCalculatorValues(totalInvestment)}</p>
                                        </div>
                                    </InvestmentFloatBox>
                                </div>
                                <div>
                                    <InvestmentFloatBox>
                                        <img src={investmentMoneyImg} alt="gains" />
                                        <div>
                                            <p className='title-font'>You Gain</p>
                                            <p className='text-ellipsis font18'>₹ {formatCalculatorValues(gains)}    <UpAGreenArrowIcon /></p>
                                        </div>
                                    </InvestmentFloatBox>
                                </div>
                            </div>
                        </Col>
                        <Col xs={24} md={16}>
                            <div style={{ height: '230px' }}>
                                <BarChart options={echartOptions} />
                            </div>
                        </Col>
                    </Row>
                </div>
            </CalculationCard>

            {isBtoCFlow() ? (customerFromSharedLinkDetails ? <InterestedUserPopup /> : null) : !isAgentEuin() ? 
                <ShareBtnContainer isMobile={isMobile}>
                    <ShareCalcButton onClick={() => setShowSharePopup(true)}><img src={whatsapp} alt="whatsapp" className="whatsappIcon" /> Share</ShareCalcButton>
                </ShareBtnContainer> : null 
            }

            {
                showSharePopup && <ShareCalculatorPopup
                    onClosePopup={() => setShowSharePopup(false)} />
            }
        </>
    )
}

export default CalculationResult